import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, TrendingUp, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
} from "recharts";

const WINDOWS = [30, 60, 90, 180];

function fmtPct(rate) {
    if (rate === null || rate === undefined || Number.isNaN(rate)) return "—";
    return `${rate.toFixed(1)}%`;
}

function tone(rate) {
    if (rate >= 70) return "bg-emerald-50 text-emerald-900 ring-emerald-200";
    if (rate >= 40) return "bg-amber-50 text-amber-900 ring-amber-200";
    if (rate > 0) return "bg-red-50 text-red-800 ring-red-200";
    return "bg-slate-100 text-slate-700 ring-slate-200";
}

export default function AdminRenewalsAnalyticsPage() {
    const [withinDays, setWithinDays] = useState(90);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get(`/admin/renewals/analytics?within_days=${withinDays}`);
            setData(payload);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [withinDays]);
    useEffect(() => { load(); }, [load]);

    const runExpirer = async () => {
        setRunning(true);
        try {
            const { data: payload } = await api.post("/admin/renewals/run-expirer");
            toast.success(`Expirer ran — ${payload.expired_count} link(s) marked expired`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRunning(false); }
    };

    const chartData = useMemo(() => {
        return (data?.weeks || []).map((w) => ({
            week: w.week_start.slice(5),
            sent: w.sent,
            signed: w.signed,
            expired: w.expired,
            rate: w.acceptance_rate,
        }));
    }, [data]);

    const totals = data?.totals || {};
    const accRate = data?.acceptance_rate ?? 0;
    const expRate = data?.expiration_rate ?? 0;

    const kpiCards = [
        {
            key: "acceptance",
            label: "Acceptance rate",
            value: fmtPct(accRate),
            icon: CheckCircle2,
            accent: tone(accRate),
        },
        {
            key: "sent",
            label: "Renewal invites sent",
            value: totals.sent ?? "—",
            icon: TrendingUp,
            accent: "bg-blue-50 text-blue-900 ring-blue-200",
        },
        {
            key: "signed",
            label: "Signed",
            value: totals.signed ?? "—",
            icon: CheckCircle2,
            accent: "bg-emerald-50 text-emerald-800 ring-emerald-200",
        },
        {
            key: "expired",
            label: "Expired without signature",
            value: totals.expired ?? "—",
            icon: AlertTriangle,
            accent: tone(100 - expRate).replace("bg-emerald", "bg-red"),
        },
        {
            key: "pending",
            label: "Pending",
            value: totals.pending ?? "—",
            icon: Clock,
            accent: "bg-slate-100 text-slate-700 ring-slate-200",
        },
    ];

    return (
        <DashboardShell>
            <div className="flex flex-col gap-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-medium text-blue-900 hover:underline mb-2"
                            data-testid="renewals-analytics-back">
                            <ArrowLeft size={14} /> Back to dashboard
                        </Link>
                        <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-slate-500">Phase 9</p>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
                            Renewal acceptance analytics
                        </h1>
                        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
                            How often customers accept renewal invites versus letting the signing link expire. The expirer cron runs daily at 03:00 UTC.
                        </p>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1.5" disabled={running}
                        onClick={runExpirer} data-testid="renewals-analytics-run-expirer">
                        <RefreshCcw size={14} className={running ? "animate-spin" : ""} />
                        {running ? "Running…" : "Run expirer now"}
                    </Button>
                </div>

                {/* Window selector */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="renewals-analytics-window-selector">
                    <span className="text-xs font-medium text-slate-600 mr-1">Window:</span>
                    {WINDOWS.map((d) => (
                        <button
                            key={d}
                            type="button"
                            onClick={() => setWithinDays(d)}
                            data-testid={`renewals-analytics-window-${d}`}
                            className={`h-8 px-3 rounded-full text-xs font-semibold ring-1 transition-colors ${
                                withinDays === d
                                    ? "bg-blue-900 text-white ring-blue-900"
                                    : "bg-white text-slate-700 ring-slate-200 hover:ring-slate-400"
                            }`}>
                            Last {d} days
                        </button>
                    ))}
                </div>

                {/* KPI tiles */}
                <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="renewals-analytics-kpis">
                    {kpiCards.map((k) => {
                        const Icon = k.icon;
                        return (
                            <Card key={k.key} className="h-full">
                                <CardContent className="p-4 flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-medium tracking-wider uppercase text-slate-500">
                                            {k.label}
                                        </p>
                                        <p className="font-display text-2xl font-bold text-slate-900 mt-1.5"
                                            data-testid={`renewals-analytics-kpi-${k.key}`}>
                                            {loading ? "…" : k.value}
                                        </p>
                                    </div>
                                    <span className={`inline-flex h-9 w-9 items-center justify-center rounded-md ring-1 ring-inset ${k.accent}`}>
                                        <Icon size={16} />
                                    </span>
                                </CardContent>
                            </Card>
                        );
                    })}
                </section>

                {/* Trend chart */}
                <Card data-testid="renewals-analytics-chart-card">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="font-display text-lg font-semibold text-slate-900">12-week trend</h2>
                                <p className="text-xs text-slate-500">Renewal invites sent vs signed vs expired, by send week.</p>
                            </div>
                        </div>
                        <div className="h-72 w-full" data-testid="renewals-analytics-chart">
                            {loading ? (
                                <div className="h-full flex items-center justify-center text-sm text-slate-500">Loading…</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#475569" }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                        <Line type="monotone" dataKey="sent" stroke="#1e3a8a" strokeWidth={2} dot={{ r: 3 }} name="Sent" />
                                        <Line type="monotone" dataKey="signed" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Signed" />
                                        <Line type="monotone" dataKey="expired" stroke="#b91c1c" strokeWidth={2} dot={{ r: 3 }} name="Expired" />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Weekly table */}
                <Card data-testid="renewals-analytics-table-card">
                    <CardContent className="p-0">
                        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
                            <h2 className="font-display text-lg font-semibold text-slate-900">Weekly breakdown</h2>
                            <p className="text-xs text-slate-500 mt-0.5">Acceptance rate per send week.</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="renewals-analytics-table">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-medium">Week starting</th>
                                        <th className="text-right px-4 py-2 font-medium">Sent</th>
                                        <th className="text-right px-4 py-2 font-medium">Signed</th>
                                        <th className="text-right px-4 py-2 font-medium">Expired</th>
                                        <th className="text-right px-4 py-2 font-medium">Acceptance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.weeks || []).map((w) => (
                                        <tr key={w.week_start} className="border-t border-slate-100 hover:bg-slate-50/60">
                                            <td className="px-4 py-2 text-slate-900">{w.week_start}</td>
                                            <td className="px-4 py-2 text-right text-slate-900">{w.sent}</td>
                                            <td className="px-4 py-2 text-right text-emerald-800">{w.signed}</td>
                                            <td className="px-4 py-2 text-right text-red-800">{w.expired}</td>
                                            <td className="px-4 py-2 text-right">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${tone(w.acceptance_rate)}`}>
                                                    {fmtPct(w.acceptance_rate)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {!loading && (!data?.weeks || data.weeks.length === 0) && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-sm">
                                                No renewal activity in this window yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </DashboardShell>
    );
}
